// volanteRenderer — Generador del SVG del volante en el cliente.
// MISMA función usada para preview en pantalla y para la conversión a PDF.
// Sin edge function en el medio → cero desync entre lo que ves y lo que descargás.

import QRCode from 'qrcode'

const LOGO_H_PLACEHOLDER = '__LOGO_HORIZONTAL_LIMPACK__'
const LOGO_RATIO = 4.45 // /public/logo-footer.png es 489x110

export const FORMATOS = {
  A4: { w: 210, h: 297 },
  A5: { w: 148, h: 210 },
  A6: { w: 105, h: 148 },
  tarjeta: { w: 90, h: 55 },
}

const PALETA = {
  navy: '#1A3461',
  navyDark: '#0F1F3D',
  teal: '#00A8B5',
  white: '#FFFFFF',
  whiteA40: 'rgba(255,255,255,0.4)',
  whiteA45: 'rgba(255,255,255,0.45)',
  whiteA55: 'rgba(255,255,255,0.55)',
  whiteA60: 'rgba(255,255,255,0.6)',
  whiteA75: 'rgba(255,255,255,0.75)',
  whiteA08: 'rgba(255,255,255,0.08)',
  whiteA05: 'rgba(255,255,255,0.05)',
  tealA12: 'rgba(0,168,181,0.14)',
  tealA30: 'rgba(0,168,181,0.32)',
  tealA50: 'rgba(0,168,181,0.55)',
}

const PADDING_X = 12
const PADDING_Y = 16

/**
 * @param {object} cfg - { formato, lote_slug, blocks: [{tipo, props}] }
 * @returns {Promise<{svg: string, dimensiones_mm: {w,h,formato}, qr_url: string}>}
 */
export async function renderVolante({ formato = 'A4', lote_slug = 'sin-slug', blocks = [] }) {
  if (!FORMATOS[formato]) throw new Error(`Formato inválido: ${formato}`)
  if (!Array.isArray(blocks) || blocks.length === 0) throw new Error('blocks vacío')

  const fmt = FORMATOS[formato]
  const W = fmt.w, H = fmt.h

  // QR real
  const qrUrl = `https://limpack.uy/r/${encodeURIComponent(lote_slug)}`
  const qrSvgRaw = await QRCode.toString(qrUrl, {
    type: 'svg', margin: 1, errorCorrectionLevel: 'H',
    color: { dark: PALETA.navy, light: '#ffffff' },
  })
  const vbMatch = qrSvgRaw.match(/viewBox="([^"]+)"/)
  const qrViewBox = vbMatch ? vbMatch[1] : '0 0 33 33'
  const qrInner = qrSvgRaw
    .replace(/<\?xml[^>]*\?>\s*/, '')
    .replace(/<svg[^>]*>/, '')
    .replace(/<\/svg>\s*$/, '')
    .trim()

  let cursorY = PADDING_Y
  const svgParts = []

  svgParts.push(`<defs>
    <linearGradient id="bg" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="${PALETA.navy}"/>
      <stop offset="100%" stop-color="${PALETA.navyDark}"/>
    </linearGradient>
    <radialGradient id="g1" cx="100%" cy="0%" r="55%">
      <stop offset="0%" stop-color="${PALETA.teal}" stop-opacity="0.16"/>
      <stop offset="100%" stop-color="${PALETA.teal}" stop-opacity="0"/>
    </radialGradient>
  </defs>`)
  svgParts.push(`<rect width="${W}" height="${H}" fill="url(#bg)"/>`)
  svgParts.push(`<rect width="${W}" height="${H}" fill="url(#g1)"/>`)

  const tieneQr = blocks.some(b => b.tipo === 'qr')
  const tieneFooter = blocks.some(b => b.tipo === 'footer')
  // El QR card tiene 38mm de alto y empieza en (H - reservaInferior - 8 - 38 + 4).
  // Quiero que los bloques de flujo TERMINEN antes del top del QR card (con margen 4mm).
  const reservaInferior = tieneQr ? 52 : 27
  const qrTopY = tieneQr ? (H - reservaInferior - 8) - 38 + 4 : H - 18
  const yMaxFlow = qrTopY - 4 // margen seguridad para no chocar con el QR
  const bloquesFlujo = blocks.filter(b => b.tipo !== 'qr' && b.tipo !== 'footer')
  const bloqueQr = blocks.find(b => b.tipo === 'qr')
  const bloqueFooter = blocks.find(b => b.tipo === 'footer')

  // Pre-calcular alturas para distribuir el sobrante entre bloques
  const alturasPre = []
  let cursorPre = PADDING_Y
  for (const b of bloquesFlujo) {
    const r = renderBlock(b.tipo, b.props || {}, cursorPre, W, formato)
    alturasPre.push(r.altura)
    cursorPre += r.altura
  }
  const totalContenido = alturasPre.reduce((a, b) => a + b, 0)
  const espacioSobrante = (yMaxFlow - PADDING_Y) - totalContenido
  // Gap entre bloques: chico para mantenerlos visualmente unidos.
  // Si sobra mucho espacio, queda al final (cerca del QR), no entre cada bloque.
  const margenExtra = espacioSobrante > 4 && bloquesFlujo.length > 1
    ? Math.min(10, espacioSobrante / (bloquesFlujo.length - 1))
    : 0

  for (let i = 0; i < bloquesFlujo.length; i++) {
    const b = bloquesFlujo[i]
    const result = renderBlock(b.tipo, b.props || {}, cursorY, W, formato)
    svgParts.push(result.svg)
    cursorY += result.altura + (i < bloquesFlujo.length - 1 ? margenExtra : 0)
    if (cursorY > yMaxFlow) break
  }
  if (bloqueQr) svgParts.push(renderQrBlock(bloqueQr.props || {}, H - reservaInferior - 8, W, qrInner, qrViewBox, lote_slug))
  svgParts.push(renderDisclaimer(H - 12, W))
  if (bloqueFooter) svgParts.push(renderFooter(H - 6, W))

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}mm" height="${H}mm" preserveAspectRatio="xMidYMid meet">
${svgParts.join('\n')}
</svg>`

  return { svg, dimensiones_mm: { w: W, h: H, formato }, qr_url: qrUrl }
}

/**
 * Reemplaza el placeholder del logo con su data URI (cargado del /public).
 */
export function inyectarLogo(svg, logoDataUri) {
  return svg.replace(/__LOGO_HORIZONTAL_LIMPACK__/g, logoDataUri)
}

// ────────── BLOCK RENDERERS ──────────

function renderBlock(tipo, props, y, W, formato) {
  switch (tipo) {
    case 'header': return renderHeader(props, y, W, formato)
    case 'hero': return renderHero(props, y, W, formato)
    case 'descuento': return renderDescuento(props, y, W)
    case 'pasos': return renderPasos(props, y, W)
    case 'lista': return renderLista(props, y, W)
    case 'testimonio': return renderTestimonio(props, y, W)
    case 'chips': return renderChips(props, y, W)
    case 'subcopy': return renderSubcopy(props, y, W)
    default: return { svg: '', altura: 0 }
  }
}

function escapeSvg(s) {
  return String(s || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&apos;')
}

function renderHeader(props, y, W, formato) {
  const compacto = formato === 'A6' || formato === 'tarjeta'
  const logoH = compacto ? 7 : 11
  const logoW = logoH * LOGO_RATIO
  const xL = PADDING_X, xR = W - PADDING_X
  let svg = `<image x="${xL}" y="${y}" width="${logoW}" height="${logoH}" href="${LOGO_H_PLACEHOLDER}" preserveAspectRatio="xMinYMid meet"/>`
  if (props.eyebrow && !compacto) {
    svg += `<text x="${xR}" y="${y + logoH * 0.65}" text-anchor="end" font-family="Inter, sans-serif" font-size="2.4" letter-spacing="0.5" font-weight="600" fill="${PALETA.whiteA55}">${escapeSvg(props.eyebrow)}</text>`
  }
  return { svg, altura: logoH + (compacto ? 4 : 7) }
}

function renderHero(props, y, W, formato) {
  const size = formato === 'A4' ? 17 : formato === 'A5' ? 13 : formato === 'A6' ? 9 : 6
  const lineHeight = size * 1.05
  let svg = '', cursorY = y + 5
  if (props.pill && formato !== 'tarjeta') {
    const pillW = (props.pill.length * 1.3) + 8
    svg += `<g><rect x="${PADDING_X}" y="${cursorY}" width="${pillW}" height="4" rx="2" fill="${PALETA.tealA12}" stroke="${PALETA.tealA30}" stroke-width="0.2"/><circle cx="${PADDING_X + 1.8}" cy="${cursorY + 2}" r="0.6" fill="${PALETA.teal}"/><text x="${PADDING_X + 3}" y="${cursorY + 2.7}" font-family="Inter, sans-serif" font-size="2.2" font-weight="700" letter-spacing="0.5" fill="${PALETA.teal}">${escapeSvg(props.pill)}</text></g>`
    cursorY += 7
  }
  const lineas = [
    { txt: props.linea1, color: PALETA.white },
    { txt: props.linea2, color: PALETA.white },
    { txt: props.linea3, color: props.linea3_teal ? PALETA.teal : PALETA.white },
  ].filter(l => l.txt)
  for (const l of lineas) {
    cursorY += lineHeight
    svg += `<text x="${PADDING_X}" y="${cursorY}" font-family="Inter, sans-serif" font-size="${size}" font-weight="900" letter-spacing="-0.6" fill="${l.color}">${escapeSvg(l.txt)}</text>`
  }
  return { svg, altura: (cursorY - y) + 4 }
}

function renderDescuento(props, y, W) {
  const cardW = (W - PADDING_X * 2 - 4) / 2
  const cardH = 38, top = y + 3
  let svg = `<rect x="${PADDING_X}" y="${top}" width="${cardW}" height="${cardH}" rx="2.5" fill="${PALETA.whiteA05}" stroke="${PALETA.whiteA08}" stroke-width="0.2"/>`
  svg += `<text x="${PADDING_X + 3}" y="${top + 6}" font-family="Inter, sans-serif" font-size="2.6" letter-spacing="0.6" fill="${PALETA.whiteA45}">${escapeSvg(props.eyebrow || 'PRECIO BASE')}</text>`
  const baseLines = wrapText(props.precio_base || '', 22)
  for (let i = 0; i < Math.min(3, baseLines.length); i++) {
    svg += `<text x="${PADDING_X + 3}" y="${top + 14 + i * 5.5}" font-family="Inter, sans-serif" font-size="4.2" font-weight="700" fill="${PALETA.white}">${escapeSvg(baseLines[i])}</text>`
  }
  const xR = PADDING_X + cardW + 4
  svg += `<rect x="${xR}" y="${top}" width="${cardW}" height="${cardH}" rx="2.5" fill="${PALETA.tealA12}" stroke="${PALETA.tealA50}" stroke-width="0.3"/>`
  svg += `<text x="${xR + 3}" y="${top + 6}" font-family="Inter, sans-serif" font-size="2.6" font-weight="700" letter-spacing="0.6" fill="${PALETA.teal}">SI SE SUMAN ${props.umbral} EN TU CUADRA</text>`
  svg += `<text x="${xR + 3}" y="${top + 25}" font-family="Inter, sans-serif" font-size="17" font-weight="900" letter-spacing="-0.6" fill="${PALETA.teal}">${props.porcentaje}%</text>`
  svg += `<text x="${xR + 22}" y="${top + 24}" font-family="Inter, sans-serif" font-size="5" font-weight="700" fill="${PALETA.white}">off</text>`
  svg += `<text x="${xR + 3}" y="${top + 33}" font-family="Inter, sans-serif" font-size="3" fill="${PALETA.whiteA75}">cada mes, para todos</text>`
  return { svg, altura: cardH + 6 }
}

function renderPasos(props, y, W) {
  const top = y + 3
  const cardH = 26
  const colW = (W - PADDING_X * 2) / 3
  let svg = `<rect x="${PADDING_X}" y="${top}" width="${W - PADDING_X * 2}" height="${cardH}" rx="2.5" fill="${PALETA.whiteA05}" stroke="${PALETA.whiteA08}" stroke-width="0.2"/>`
  const items = [
    { num: props.p1_num, label: props.p1_label, color: PALETA.white },
    { num: props.p2_num, label: props.p2_label, color: PALETA.white },
    { num: props.p3_num, label: props.p3_label, color: PALETA.teal },
  ]
  for (let i = 0; i < 3; i++) {
    const cx = PADDING_X + colW * i + colW / 2
    svg += `<text x="${cx}" y="${top + 12}" text-anchor="middle" font-family="Inter, sans-serif" font-size="9" font-weight="900" fill="${items[i].color}">${escapeSvg(items[i].num)}</text>`
    svg += `<text x="${cx}" y="${top + 20}" text-anchor="middle" font-family="Inter, sans-serif" font-size="2.8" fill="${PALETA.whiteA45}">${escapeSvg(items[i].label || '')}</text>`
    if (i < 2) {
      const ax = PADDING_X + colW * (i + 1)
      svg += `<text x="${ax}" y="${top + 12}" text-anchor="middle" font-family="Inter, sans-serif" font-size="5.5" fill="${PALETA.tealA50}">→</text>`
    }
  }
  return { svg, altura: cardH + 5 }
}

function renderLista(props, y, W) {
  const top = y + 4
  const items = Array.isArray(props.items) ? props.items.slice(0, 8) : []
  const cardH = 8 + Math.ceil(items.length / 2) * 4.5
  let svg = `<rect x="${PADDING_X}" y="${top}" width="${W - PADDING_X * 2}" height="${cardH}" rx="2.5" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.1)" stroke-width="0.2"/>`
  svg += `<text x="${PADDING_X + 3}" y="${top + 4}" font-family="Inter, sans-serif" font-size="2.2" letter-spacing="0.6" fill="${PALETA.whiteA45}">TU LISTA DE COMPRAS</text>`
  svg += `<text x="${W - PADDING_X - 3}" y="${top + 4}" text-anchor="end" font-family="Inter, sans-serif" font-size="2.2" letter-spacing="0.6" fill="${PALETA.whiteA45}">MENSUAL</text>`
  const colW = (W - PADDING_X * 2 - 6) / 2
  for (let i = 0; i < items.length; i++) {
    const col = i % 2, row = Math.floor(i / 2)
    const ix = PADDING_X + 3 + col * colW
    const iy = top + 9 + row * 4.5
    svg += `<text x="${ix}" y="${iy}" font-family="Inter, sans-serif" font-size="3.2" fill="${PALETA.white}">${escapeSvg(items[i])}</text>`
    svg += `<text x="${ix + colW - 1}" y="${iy}" text-anchor="end" font-family="Inter, sans-serif" font-size="2.8" fill="${PALETA.teal}">✓</text>`
  }
  return { svg, altura: cardH + 6 }
}

function renderTestimonio(props, y, W) {
  const top = y + 4, cardH = 25
  let svg = `<rect x="${PADDING_X}" y="${top}" width="${W - PADDING_X * 2}" height="${cardH}" rx="2.5" fill="${PALETA.whiteA05}" stroke="${PALETA.whiteA08}" stroke-width="0.2"/>`
  svg += `<text x="${PADDING_X + 3}" y="${top + 8}" font-family="Georgia, serif" font-size="11" fill="${PALETA.teal}">"</text>`
  const lines = wrapText(props.quote || '', 50)
  for (let i = 0; i < Math.min(2, lines.length); i++) {
    svg += `<text x="${PADDING_X + 8}" y="${top + 8 + i * 5}" font-family="Inter, sans-serif" font-size="3.5" font-weight="600" fill="${PALETA.white}">${escapeSvg(lines[i])}</text>`
  }
  svg += `<text x="${PADDING_X + 3}" y="${top + cardH - 3}" font-family="Inter, sans-serif" font-size="2.5" letter-spacing="0.5" fill="${PALETA.whiteA45}">— ${escapeSvg((props.atrib || '').toUpperCase())}</text>`
  return { svg, altura: cardH + 5 }
}

function renderChips(props, y, W) {
  const top = y + 3
  const chips = Array.isArray(props.chips) ? props.chips : []
  let x = PADDING_X, svg = ''
  for (const c of chips) {
    const w = c.length * 1.6 + 6
    if (x + w > W - PADDING_X) x = PADDING_X
    svg += `<rect x="${x}" y="${top}" width="${w}" height="5" rx="2.5" fill="${PALETA.tealA12}" stroke="${PALETA.tealA30}" stroke-width="0.15"/>`
    svg += `<text x="${x + 3}" y="${top + 3.5}" font-family="Inter, sans-serif" font-size="2.4" fill="${PALETA.white}">✓ ${escapeSvg(c)}</text>`
    x += w + 1.5
  }
  return { svg, altura: 10 }
}

function renderSubcopy(props, y, W) {
  const lines = wrapText(props.texto || '', 70)
  let svg = ''
  for (let i = 0; i < Math.min(4, lines.length); i++) {
    svg += `<text x="${PADDING_X}" y="${y + 5 + i * 4.2}" font-family="Inter, sans-serif" font-size="3" fill="${PALETA.whiteA75}">${escapeSvg(lines[i])}</text>`
  }
  return { svg, altura: Math.min(4, lines.length) * 4.2 + 6 }
}

function renderQrBlock(props, y, W, qrInner, qrViewBox, slug) {
  const cardH = 38, qrSize = 30, cardY = y - cardH + 4
  let svg = `<rect x="${PADDING_X}" y="${cardY}" width="${W - PADDING_X * 2}" height="${cardH}" rx="2.5" fill="${PALETA.whiteA05}" stroke="${PALETA.tealA30}" stroke-width="0.3"/>`
  svg += `<rect x="${PADDING_X + 2}" y="${cardY + 4}" width="${qrSize + 2}" height="${qrSize + 2}" fill="white" rx="1"/>`
  svg += `<svg x="${PADDING_X + 3}" y="${cardY + 5}" width="${qrSize}" height="${qrSize}" viewBox="${qrViewBox}" preserveAspectRatio="xMidYMid meet">${qrInner}</svg>`
  const tx = PADDING_X + qrSize + 8
  svg += `<text x="${tx}" y="${cardY + 7}" font-family="Inter, sans-serif" font-size="2.4" letter-spacing="0.6" fill="${PALETA.whiteA45}">${escapeSvg(props.eyebrow || 'SUMATE A LA LISTA')}</text>`
  svg += `<text x="${tx}" y="${cardY + 12.5}" font-family="Inter, sans-serif" font-size="5" font-weight="900" fill="${PALETA.white}">${escapeSvg(props.titulo || 'Escaneá. Sin compromiso.')}</text>`
  const bajadaLines = wrapText(props.bajada || '', 36)
  for (let i = 0; i < Math.min(3, bajadaLines.length); i++) {
    svg += `<text x="${tx}" y="${cardY + 17 + i * 3.5}" font-family="Inter, sans-serif" font-size="2.7" fill="${PALETA.whiteA60}">${escapeSvg(bajadaLines[i])}</text>`
  }
  const url = `limpack.uy/r/${slug}`
  const pillW = url.length * 1.4 + 4
  svg += `<rect x="${tx}" y="${cardY + cardH - 7}" width="${pillW}" height="4.5" rx="1" fill="${PALETA.tealA12}"/>`
  svg += `<text x="${tx + 2}" y="${cardY + cardH - 4}" font-family="SF Mono, Monaco, monospace" font-size="2.5" fill="${PALETA.teal}">${escapeSvg(url)}</text>`
  return svg
}

function renderDisclaimer(y, W) {
  return `<text x="${W / 2}" y="${y}" text-anchor="middle" font-family="Inter, sans-serif" font-size="2" letter-spacing="0.3" fill="${PALETA.whiteA40}" font-style="italic">* Descuento colaborativo aplica únicamente a suscripción de kits mensuales.</text>`
}

function renderFooter(y, W) {
  return `<line x1="${PADDING_X}" y1="${y - 4}" x2="${W - PADDING_X}" y2="${y - 4}" stroke="rgba(255,255,255,0.08)" stroke-width="0.15"/><text x="${W / 2}" y="${y}" text-anchor="middle" font-family="Inter, sans-serif" font-size="2.1" letter-spacing="0.8" fill="${PALETA.whiteA40}">@LIMPACKUY · MONTEVIDEO · LIMPACK.UY</text>`
}

function wrapText(text, maxChars) {
  if (!text) return []
  const words = String(text).split(/\s+/)
  const lines = []
  let cur = ''
  for (const w of words) {
    if ((cur + ' ' + w).trim().length > maxChars) {
      if (cur) lines.push(cur)
      cur = w
    } else {
      cur = cur ? cur + ' ' + w : w
    }
  }
  if (cur) lines.push(cur)
  return lines
}
